import React, { useState } from 'react';
import { Tooltip } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { Character, CharacterFeatureSource } from "@dndModels";

import CharacterAbilityScoresInput from "@dndComponents/character/ability_scores/CharacterAbilityScoresInput";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import FloatMenu from "@components/input/menus/FloatMenu";
import StringBox from "@components/input/strings/StringBox";
import CharacterFeatureFromSource from "@dndComponents/character/features/CharacterFeatureFromSource";

interface Props {
  character: Character;
  className: string;
  disabled: boolean;
  onChange: () => void;
}

const customization_map: { [type: string]: string } = {
  // "Override Proficiencies": "Proficiency Override",
  "Extra Modifiers": "Modifier",
  "Override": "Override"
};

export default function CharacterAbilityScoresButton({
  character,
  className,
  disabled,
  onChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("Base");

  const renderExtras = () => {
    return (
      <ModalOrDrawer
        open={open}
        onClose={() => { 
          setOpen(false);
          character.subscription_holder.modalClosed(); 
        }}
      >
        { open && renderAbilityScoresInput() }
      </ModalOrDrawer>
    );
  }

  const renderBase = () => {
    return (
      <CharacterAbilityScoresInput
        character={character}
        onChange={onChange}
      /> 
    );
  }

  const renderASIFeatures = () => {
    const asi_features = [...character.asi_features];
    // console.log(asi_features);
    // const cfb_ids = asi_features.map(o => o)
    return (
      <>
        <Grid
          className="header"
          size={{
            xs: 12
          }}>
          Ability Score Features
        </Grid>
        { asi_features.sort((a,b) => { return a.name.localeCompare(b.name) }).map((char_feature, key) => {
          const source = character.all_char_feature_sources.find(o => 
            o.true_id === char_feature.source_true_id);
          if (source) {
            return (
              <Grid
                key={key}
                size={{
                  xs: 12
                }}>
                <CharacterFeatureFromSource
                  character={character}
                  obj={char_feature}
                  source={source}
                />
              </Grid>
            );
          }
          return null;
        })}
      </>
    );
  }

  const renderAbilityScoreCustomizations = () => {
    return (
      <>
        <Grid
          style={{ fontWeight: "bold" }}
          size={{
            xs: 12
          }}>
          { view }
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { renderAbilityScoreCustomization("Strength", customization_map[view]) }
          { renderAbilityScoreCustomization("Dexterity", customization_map[view]) }
          { renderAbilityScoreCustomization("Constitution", customization_map[view]) }
          { renderAbilityScoreCustomization("Intelligence", customization_map[view]) }
          { renderAbilityScoreCustomization("Wisdom", customization_map[view]) }
          { renderAbilityScoreCustomization("Charisma", customization_map[view]) }
        </Grid>
      </>
    );
  }

  const renderAbilityScoreCustomization = (ability_score: string, customization_type: string) => {
    // debugger;
    const parent = character.ability_score_customizations[ability_score];
    const customization = parent ? parent[customization_type] : null;
    return (
      <>
        <Grid
          size={{
            xs: 3
          }}>
          { ability_score }
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          { character.current_ability_scores.getAbilityScore(ability_score) }
        </Grid>
        <Grid
          size={{
            xs: customization && customization.value !== null ? 4 : 6
          }}>
          <StringBox
            name="Value"
            value={ customization && customization.value !== null ? `${customization.value}` : "" }
            onBlur={(changed: string) => {
              if (!character.ability_score_customizations[ability_score]) {
                character.ability_score_customizations[ability_score] = { [customization_type]: { value: null, notes: "" }};
              } else if (!character.ability_score_customizations[ability_score][customization_type]) {
                character.ability_score_customizations[ability_score][customization_type] = { value: null, notes: "" };
              }
              if (changed === "") {
                delete character.ability_score_customizations[ability_score][customization_type];
              } else {
                character.ability_score_customizations[ability_score][customization_type].value = +changed;
              }
              onChange();
            }}
          />
        </Grid>
        { customization && customization.value !== null &&
          <Grid
            size={{
              xs: 2
            }}>
            <StringBox
              name={ `Source/Notes` }
              value={ customization ? customization.notes : "" }
              onBlur={(changed: string) => {
                if (!character.ability_score_customizations[ability_score]) {
                  character.ability_score_customizations[ability_score] = { [customization_type]: { value: null, notes: "" }};
                } else if (!character.ability_score_customizations[ability_score][customization_type]) {
                  character.ability_score_customizations[ability_score][customization_type] = { value: null, notes: "" };
                }
                character.ability_score_customizations[ability_score][customization_type].notes = changed;
                onChange();
              }}
            />
          </Grid>
        }
      </>
    );
  }

  const renderView = () => {
    switch(view) {
      case "Base": return renderBase();
      case "Features": return renderASIFeatures();
      default: return renderAbilityScoreCustomizations();
    }
  }

  const renderAbilityScoresInput = () => {
    return (
      <Grid
        container
        spacing={0}
        direction="row"
        size={{
          xs: 12
        }}>
        <Grid
          className="centered header"
          size={{
            xs: 12
          }}>
          Manage Ability Scores
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <FloatMenu
            options={["Base","Features","Override","Extra Modifiers"]}
            selected={view}
            onSelect={setView}
          />
        </Grid>
        { renderView() }
      </Grid>
    );
  }

  if (disabled) {
    return (
      <div className={ `header centered ${className}`}>
        Ability Scores
      </div>
    ); 
  } else {
    return (
      <>
        <Tooltip title={`Change Ability Scores`}>
          <div className={ `clickable header centered ${className}`}
            onClick={() => { setOpen(true); }}>
            Ability Scores
          </div>
        </Tooltip>
        { renderExtras() }
      </>
    ); 
  }
}

